import React from "react";
import { services, caseStudies, specializedServices } from "../mock";
import { Link } from "react-router-dom";
import { ArrowRight, Quote, Sprout, ShieldAlert, Leaf, Search, TestTube, Scale, Settings, PackageX, Recycle, Lock } from "lucide-react";
import useSeo from "../hooks/useSeo";

const specIcons = { Sprout, ShieldAlert, Leaf, Search, TestTube, Scale, Settings, PackageX, Recycle };

const Services = () => {
  useSeo({
    title: "Services",
    description:
      "Research, Finance and Auction services delivered with global standards. Explore case studies and outcomes from Astoria Solutions engagements.",
    path: "/services",
  });
  return (
    <>
      <section className="bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h1 className="text-4xl md:text-6xl font-extrabold">Our Services</h1>
          <p className="mt-4 text-teal-400">Research · Finance · Auction — with global standards.</p>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6 space-y-24">
          {services.map((s, i) => (
            <div key={s.id} id={s.id} className="scroll-mt-24">
              <div
                className={`grid grid-cols-1 md:grid-cols-2 gap-10 items-center ${
                  i % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="relative">
                  <div className="absolute -inset-3 bg-teal-200/40 rounded-2xl -z-10" />
                  <img src={s.image} alt={s.title} className="rounded-2xl w-full h-[320px] object-cover shadow-lg" />
                </div>
                <div>
                  <div className="w-10 h-1 bg-teal-500 rounded-full" />
                  <h2 className="mt-4 text-3xl md:text-4xl font-extrabold text-neutral-900">{s.title}</h2>
                  <p className="mt-4 text-neutral-600 leading-relaxed">{s.description}</p>
                  <p className="mt-3 text-neutral-500 leading-relaxed">{s.long}</p>
                  <Link
                    to="/contact"
                    className="mt-6 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-6 py-3 rounded-full transition-colors"
                  >
                    Get Started <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* CASE STUDIES */}
              <div className="mt-14">
                <div className="flex items-baseline justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-teal-500 font-bold tracking-widest text-xs">CASE STUDIES</p>
                    <h3 className="mt-2 text-2xl md:text-3xl font-bold text-neutral-900">
                      Client wins in {s.title}
                    </h3>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {caseStudies[s.id].map((c) => (
                    <Link
                      to={`/case-studies/${c.slug}`}
                      key={c.title}
                      className="group bg-neutral-50 rounded-2xl overflow-hidden border border-neutral-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img src={c.image} alt={c.client} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        <div className="absolute top-3 left-3 bg-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          {c.client}
                        </div>
                        {c.premium && (
                          <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-amber-500 text-black text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full">
                            <Lock className="w-3 h-3" /> PREMIUM
                          </div>
                        )}
                      </div>
                      <div className="p-6">
                        <div className="flex items-center gap-4">
                          <div className="text-2xl font-extrabold text-teal-600">{c.metric}</div>
                          <div className="text-xs uppercase tracking-wider text-neutral-500">{c.metricLabel}</div>
                        </div>
                        <h4 className="mt-3 text-lg font-bold text-neutral-900 leading-snug">{c.title}</h4>
                        <div className="mt-4 relative bg-white rounded-xl p-4 border border-neutral-100">
                          <Quote className="absolute -top-2 -left-2 w-5 h-5 text-teal-500" />
                          <p className="text-sm text-neutral-600 italic leading-relaxed">“{c.quote}”</p>
                          <p className="mt-2 text-xs font-semibold text-neutral-800">— {c.author}</p>
                        </div>
                        <div className="mt-4 inline-flex items-center gap-1 text-teal-600 text-sm font-semibold group-hover:gap-2 transition-all">
                          Read the story <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SPECIALIZED SERVICES */}
      <section className="bg-neutral-50 py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-teal-500 font-bold tracking-widest text-sm">SPECIALIZED SERVICES</p>
            <h2 className="mt-2 text-3xl md:text-4xl font-extrabold text-neutral-900">
              Testing, inspection, safety and compliance advisory
            </h2>
            <p className="mt-3 text-neutral-600">
              A deeper toolkit of specialized services we offer alongside our core Research, Finance and Auction practices.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {specializedServices.map((s) => {
              const Icon = specIcons[s.icon] || Sprout;
              return (
                <article
                  key={s.id}
                  className="group bg-white rounded-2xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 left-4 w-11 h-11 rounded-full bg-teal-500 flex items-center justify-center shadow-lg">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-neutral-900">{s.title}</h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.body}</p>
                    <Link
                      to="/contact"
                      className="mt-4 inline-flex items-center gap-1 text-teal-600 text-sm font-semibold hover:gap-2 transition-all"
                    >
                      Enquire <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;
